/**
 * Remembers what each photo turned out to be, so re-adding the same picture is
 * free and instant.
 *
 * Kept in its own store rather than mixed into the inventory: it's disposable,
 * and clearing it must never touch a user's food.
 *
 * Now IndexedDB, with the same synchronous front door the inventory has and for
 * the same reason — `readCachedRecognition` is called on the path that decides
 * whether to spend an API call, and that decision cannot wait on a promise
 * without restructuring the caller. Reads come from memory; writes go to memory
 * and then to disk.
 */

import { db, hasIndexedDb } from "../../lib/storage/db";
import type { Recognition } from "./types";

const CACHE_KEY = "pantrymind.vision.v1";
/**
 * Entries are tiny (~100 bytes), so a hundred of them costs ~10KB — small next
 * to the photos sharing the same quota, and enough to cover a shopping trip.
 */
const MAX_ENTRIES = 100;

interface CacheEntry {
  hash: string;
  /**
   * A photo can hold several things, so this is a list. Entries written before
   * multi-item scanning held a single object under `result`; those are still
   * read, because a cache that resets on upgrade costs real API calls.
   */
  results: Recognition[];
}

/** In memory, oldest first. Hydrated at startup, written through on change. */
let entries: CacheEntry[] = [];

/**
 * Loads the cache. Never rejects — a cache that fails to load is a cache miss,
 * which costs one API call and nothing else.
 */
export async function hydrateRecognitionCache(): Promise<void> {
  if (!hasIndexedDb()) return;
  try {
    const stored = await (await db()).getAll("vision");
    entries = stored
      .sort((a, b) => a.at - b.at)
      .flatMap((entry) =>
        Array.isArray(entry.results) && entry.results.length > 0
          ? [{ hash: entry.hash, results: entry.results }]
          : []
      );
  } catch {
    entries = [];
  }
}

/** Reads the pre-IndexedDB shape, for the one-time migration. */
export function readLegacyCache(): CacheEntry[] {
  try {
    const raw = localStorage.getItem(CACHE_KEY);
    if (!raw) return [];

    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];

    return parsed.flatMap((entry): CacheEntry[] => {
      if (typeof entry !== "object" || entry === null) return [];
      const record = entry as Record<string, unknown>;
      if (typeof record.hash !== "string") return [];

      if (Array.isArray(record.results)) {
        const results = record.results.filter(
          (r): r is Recognition => typeof (r as Recognition)?.name === "string"
        );
        return results.length > 0 ? [{ hash: record.hash, results }] : [];
      }

      // The old single-object shape.
      const legacy = record.result as Recognition | undefined;
      return typeof legacy?.name === "string"
        ? [{ hash: record.hash, results: [legacy] }]
        : [];
    });
  } catch {
    return [];
  }
}

export function readCachedRecognition(hash: string): Recognition[] | null {
  return entries.find((entry) => entry.hash === hash)?.results ?? null;
}

export function writeCachedRecognition(hash: string, results: Recognition[]): void {
  // Oldest entries fall off the front once the cap is reached.
  entries = [...entries.filter((entry) => entry.hash !== hash), { hash, results }].slice(
    -MAX_ENTRIES
  );
  void persist(hash, results);
}

/**
 * Writes run one after another, not all at once.
 *
 * Each write also trims the store, which means reading it — and thirty
 * unserialised writes each reading the whole store and deciding what to delete
 * is both wasteful and racy: they disagree about what "the oldest hundred"
 * means and can leave more than a hundred behind.
 */
let queue: Promise<void> = Promise.resolve();

function persist(hash: string, results: Recognition[]): Promise<void> {
  queue = queue.then(() => write(hash, results));
  return queue;
}

/** Exposed so a test can wait for the queue rather than guessing at a delay. */
export function cacheWritesSettled(): Promise<void> {
  return queue;
}

async function write(hash: string, results: Recognition[]): Promise<void> {
  if (!hasIndexedDb()) return;
  try {
    const database = await db();
    await database.put("vision", { hash, results, at: Date.now() });

    // Trim on disk too, or the store grows without bound while memory stays
    // capped — and the next hydration would load all of it.
    const all = await database.getAll("vision");
    if (all.length > MAX_ENTRIES) {
      const doomed = all.sort((a, b) => a.at - b.at).slice(0, all.length - MAX_ENTRIES);
      const tx = database.transaction("vision", "readwrite");
      for (const entry of doomed) await tx.store.delete(entry.hash);
      await tx.done;
    }
  } catch {
    // A failed write costs one extra API call next time. The cache is an
    // optimisation and must never break adding an item.
  }
}

/** Exposed so a settings screen can offer to clear it. */
export function clearRecognitionCache(): void {
  entries = [];
  void (async () => {
    try {
      (await db()).clear("vision");
    } catch {
      // Disposable by definition.
    }
  })();
  try {
    // The pre-IndexedDB copy is left behind by the migration on purpose; a
    // "clear" that leaves it lying there would be a lie.
    localStorage.removeItem(CACHE_KEY);
  } catch {
    /* nothing to do */
  }
}

/** Tests only. */
export function resetRecognitionCacheForTests(): void {
  entries = [];
}
